import { useEffect, useState } from 'react';
import { TextArea, Typography, TypographyType } from '../../components';
import { useAppSelector } from '../../hooks';
import { BasePracticeProspect, TextColor } from '../../types';
import {
  formatDisplayedPhoneNumber,
  generateCandidateEmailContent,
  getDisplayedContent,
  getFormattedContent,
  isValidEmail,
  isValidPhoneNumber,
} from '../../utils';

interface InvitationMessageProps {
  countryCode: string;
  email: string;
  emailContent: string;
  error: boolean;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  prospects: BasePracticeProspect[];
  resetError: () => void;
  setEmailContent: React.Dispatch<React.SetStateAction<string>>;
  selectedProspectData?: BasePracticeProspect;
}

const InvitationMessage = ({
  countryCode,
  email,
  emailContent,
  error,
  firstName,
  lastName,
  phoneNumber,
  prospects,
  resetError,
  setEmailContent,
  selectedProspectData,
}: InvitationMessageProps) => {
  const [isEmailContentCustomized, setIsEmailContentCustomized] = useState(false);

  const organization = useAppSelector((state) => state.auth.organization);

  if (!organization) return null;

  useEffect(() => {
    // If content is customized by user or no prospect is selected, do not generate content
    if (isEmailContentCustomized || !selectedProspectData) {
      return;
    }

    // Initial content generation, check on empty or invalid content
    if (
      !emailContent.trim().length &&
      (!firstName ||
        !lastName ||
        !phoneNumber ||
        !email ||
        !isValidPhoneNumber(countryCode + phoneNumber) ||
        !isValidEmail(email))
    ) {
      return;
    }

    // Generate or update content
    const generatedContent = generateCandidateEmailContent(
      `${firstName} ${lastName}`,
      formatDisplayedPhoneNumber(countryCode + phoneNumber),
      organization.name,
      selectedProspectData
    );

    const formattedContent = getFormattedContent(generatedContent);
    setEmailContent(formattedContent);
  }, [
    firstName,
    lastName,
    phoneNumber,
    email,
    countryCode,
    prospects,
    organization,
    isEmailContentCustomized,
    setEmailContent,
    selectedProspectData,
    emailContent,
  ]);

  const handleEmailContentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    // Replace new lines with <br />
    const formattedContent = getFormattedContent(e.target.value);
    setEmailContent(formattedContent);
    // To disable the auto-generated content
    setIsEmailContentCustomized(true);
    // Reset error
    resetError();
  };

  return (
    <div className="flex flex-col">
      <TextArea
        placeholder="Invitation message*"
        onChange={handleEmailContentChange}
        value={getDisplayedContent(emailContent)}
        error={!!error}
        rows={20}
      />
      <div className="align-center flex flex-col gap-1 p-2 px-1">
        <Typography type={TypographyType.X_SMALL} color={TextColor.SECONDARY}>
          Simulation phone number will be included in the email automatically.
        </Typography>
      </div>
    </div>
  );
};

export default InvitationMessage;
